import { http } from '@/api/http';

/**
 * A per-id cache of object-URLs for JWT-guarded binary endpoints. `<img src>` / `<a href>` can't send
 * a Bearer token, so bytes are fetched via axios and swapped in as object-URLs. `get(id)` fetches once
 * and memoises the object-URL; `clearAll()` revokes every URL (e.g. on unmount / client switch).
 */
export function createBlobUrlCache(fetcher: (id: string) => Promise<Blob>) {
  const cache = new Map<string, string>();
  return {
    async get(id: string): Promise<string> {
      const cached = cache.get(id);
      if (cached) return cached;
      const blob = await fetcher(id);
      const url = URL.createObjectURL(blob);
      cache.set(id, url);
      return url;
    },
    clearAll(): void {
      for (const url of cache.values()) URL.revokeObjectURL(url);
      cache.clear();
    },
  };
}

/** A Blob's text — `Blob.text()` where the runtime has it (older WebKit does not), `FileReader` otherwise. */
function blobText(b: Blob): Promise<string> {
  if (typeof b.text === 'function') return b.text();
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result));
    r.onerror = () => reject(r.error);
    r.readAsText(b);
  });
}

/**
 * The file name the server put in `Content-Disposition` (`filename*=UTF-8''…` first, then `filename="…"`), or null.
 */
export function attachmentFilename(header: string | undefined | null): string | null {
  if (!header) return null;
  const star = header.match(/filename\*=UTF-8''([^;]+)/i);
  if (star) {
    try { return decodeURIComponent(star[1].trim()); } catch { /* fall through to the plain form */ }
  }
  const plain = header.match(/(?:^|;)\s*filename="?([^";]+)"?/i);
  return plain ? plain[1].trim() : null;
}

/**
 * Fetches a file as a Blob from a JWT endpoint and triggers a browser download. The name is the server's
 * (`Content-Disposition`) when it sends one, else `filename`.
 *
 * An error body also arrives as a Blob (the request asked for one), so it is read back as JSON before the error is
 * re-thrown — otherwise the caller could never see the server's code and the client got "something went wrong"
 * instead of "the period is too long".
 */
export async function downloadBlob(
  url: string,
  params: Record<string, string>,
  filename: string,
): Promise<void> {
  let res;
  try {
    res = await http.get(url, { params, responseType: 'blob' });
  } catch (e: any) {
    const data = e?.response?.data;
    if (typeof Blob !== 'undefined' && data instanceof Blob) {
      try { e.response.data = JSON.parse(await blobText(data)); } catch { /* not JSON — leave it as it came */ }
    }
    throw e;
  }
  const blobUrl = URL.createObjectURL(res.data as Blob);
  const a = document.createElement('a');
  a.href = blobUrl;
  a.download = attachmentFilename(res.headers?.['content-disposition']) ?? filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(blobUrl);
}
